import { useMsal } from '@azure/msal-react';
import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useLinkContext } from '@src/main/ui/providers/link/LinkContextProvider';
import { useUserContext } from '@src/main/ui/providers/user/UserContextProvider';

import LinkFormModal from '@src/main/ui/components/LinkFormModal/LinkFormModal';
import Icon from '@src/main/ui/components/common/Icon/Icon';
import ThemeMenu from '@src/main/ui/components/layout/common/UserMenu/theme/ThemeMenu';
import { getIconId } from '../../../common/Icon/IconId';
import styles from './UserMenu.module.scss';

const UserMenu: FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElTheme, setAnchorElTheme] = useState<null | HTMLElement>(null);
  const [openLinkFormModal, setOpenLinkFormModal] = useState(false);
  const { link, updateLink } = useLinkContext();
  const { instance } = useMsal();
  const { user } = useUserContext();
  const navigate = useNavigate();

  const [linkForm, setLinkForm] = useState('');

  useEffect(() => {
    if (link) {
      setLinkForm(link);
    }
  }, [link]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuTheme = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElTheme(event.currentTarget);
  };

  const handleCloseTheme = () => {
    setAnchorElTheme(null);
  };

  const handleAddDashboards = () => {
    navigate('/admin/add-dashboards/areas');
    handleClose();
  };

  const handleManageUsers = () => {
    navigate('/admin/manage-admin-users');
    handleClose();
  };

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  const openForm = () => {
    const anchor = document.createElement('a');
    anchor.href = linkForm;
    anchor.target = '_blank';
    anchor.click();
  };

  return (
    <>
      <div className={styles.userMenu}>
        <IconButton sx={{ fontSize: '35px' }} onClick={handleMenu}>
          <Icon iconId="account" />
        </IconButton>
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          disableScrollLock
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {[
            { label: 'Theme', iconId: 'theme', action: handleMenuTheme },
            ...(user?.roles?.includes('ADMIN')
              ? [
                  {
                    label: 'Add dashboards',
                    iconId: 'add',
                    action: handleAddDashboards,
                  },
                  {
                    label: 'Manage Users',
                    iconId: 'account',
                    action: handleManageUsers,
                  },
                  {
                    label: 'Edit Form Link',
                    iconId: 'edit',
                    action: () => setOpenLinkFormModal(true),
                  },
                ]
              : []),
            {
              label: 'Request dashboard',
              iconId: 'info',
              action: () => openForm(),
            },
            { label: 'Logout', iconId: 'logout', action: handleLogout },
          ].map((menuItem, index) => (
            <MenuItem key={menuItem.label} onClick={menuItem.action}>
              <ListItemIcon className={styles[`userMenu__item__icon_${index}`]}>
                <Icon iconId={getIconId(menuItem.iconId)} />
              </ListItemIcon>
              <ListItemText>{menuItem.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
        <ThemeMenu
          anchorElTheme={anchorElTheme}
          handleCloseTheme={() => {
            handleClose();
            handleCloseTheme();
          }}
        />
      </div>

      <LinkFormModal
        open={openLinkFormModal}
        link={link}
        updateLink={updateLink}
        onClose={() => setOpenLinkFormModal(false)}
      />
    </>
  );
};

export default UserMenu;
